import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowPathIcon, CircleStackIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { usePageSize } from '../../hooks/usePageSize';
import { PageSizeSelect } from '../../components/ui/PageSizeSelect';
import { api } from '../../services/api';

function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toString();
}

function formatRelative(dateStr: string | null): string {
  if (!dateStr) return '\u2014';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-slate-700 bg-slate-800/50 p-4">
      <div className="text-xs text-slate-500 uppercase tracking-wide">{label}</div>
      <div className="text-2xl font-bold text-white mt-1">{value}</div>
    </div>
  );
}

export function AdminNeptuneDevicesPage() {
  // Return address for the device detail page's back button.
  const location = useLocation();
  const backTo = location.pathname + location.search;
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [pageSize, setPageSize] = usePageSize('dataPortal');

  useEffect(() => {
    setPage(1);
  }, [pageSize, search]);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin', 'neptune', 'devices', page, pageSize, search],
    queryFn: () =>
      api.adminGetNeptuneDevices({
        page,
        limit: pageSize,
        search: search || undefined,
      }),
  });

  const totalPages = Math.ceil((data?.total ?? 0) / pageSize);
  const stats = data?.stats;

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-white">Data Portal</h1>
          <p className="text-sm text-slate-400">
            Device telemetry data summary{data ? ` \u2014 ${data.total} devices with data` : ''}
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-2 rounded-lg bg-slate-700 px-3 py-2 text-sm text-slate-300 hover:bg-slate-600"
        >
          <ArrowPathIcon className="h-4 w-4" />
          Refresh
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Devices" value={stats.totalDevices.toString()} />
          <StatCard label="NAV Rows" value={formatCount(stats.totalNav)} />
          <StatCard label="AIS Rows" value={formatCount(stats.totalAis)} />
          <StatCard label="Raw Rows" value={formatCount(stats.totalRaw)} />
        </div>
      )}

      {/* Search */}
      <div className="flex gap-2">
        <div className="relative flex-1 max-w-sm">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <input
            type="text"
            placeholder="Search by device name or serial..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="w-full rounded-lg border border-slate-700 bg-slate-800 pl-9 pr-3 py-2 text-sm text-slate-300 placeholder-slate-500 focus:border-slate-500 focus:outline-none"
          />
        </div>
        <button
          onClick={handleSearch}
          className="rounded-lg bg-slate-700 px-4 py-2 text-sm text-slate-300 hover:bg-slate-600"
        >
          Search
        </button>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <ArrowPathIcon className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      )}

      {isError && (
        <div className="rounded-lg border border-red-800 bg-red-900/20 p-4 text-sm text-red-400">
          Failed to load device data.{' '}
          <button className="underline" onClick={() => refetch()}>Retry</button>
        </div>
      )}

      {data && (
        <>
          <div className="overflow-x-auto rounded-lg border border-slate-700">
            <table className="w-full text-sm text-left">
              <thead className="bg-slate-800/50 text-xs text-slate-400">
                <tr>
                  <th className="px-4 py-3 font-medium">Device</th>
                  <th className="px-4 py-3 font-medium">MMSI</th>
                  <th className="px-4 py-3 font-medium">Owner</th>
                  <th className="px-4 py-3 font-medium">Last Seen</th>
                  <th className="px-4 py-3 font-medium text-right">NAV</th>
                  <th className="px-4 py-3 font-medium text-right">AIS</th>
                  <th className="px-4 py-3 font-medium text-right">Raw</th>
                  <th className="px-4 py-3 font-medium text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {data.devices.map((device: any) => {
                  const total = device.navCount + device.aisObservationCount + device.rawSentenceCount;
                  return (
                    <tr key={device.id} className="border-t border-slate-700/50 hover:bg-slate-700/30">
                      <td className="px-4 py-3">
                        <Link
                          to={`/admin/devices/${device.id}`}
                          state={{ from: backTo }}
                          className="text-slate-200 font-medium hover:text-cyan-400 hover:underline"
                          title="Open this device's detail page"
                        >
                          {device.name || device.serialNumber || device.id.slice(0, 12)}
                        </Link>
                        <div className="text-slate-500 text-xs font-mono">{device.id.slice(0, 8)}...{device.id.slice(-4)}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-400 font-mono text-xs">
                        {device.vesselMmsi || '\u2014'}
                      </td>
                      <td className="px-4 py-3 text-slate-400 text-xs">
                        {device.ownerEmail || '\u2014'}
                      </td>
                      <td className="px-4 py-3 text-slate-400 whitespace-nowrap text-xs">
                        {formatRelative(device.lastSeenAt)}
                      </td>
                      <td className="px-4 py-3 text-right text-slate-300 font-mono">
                        {formatCount(device.navCount)}
                      </td>
                      <td className="px-4 py-3 text-right text-slate-300 font-mono">
                        {formatCount(device.aisObservationCount)}
                      </td>
                      <td className="px-4 py-3 text-right text-slate-300 font-mono">
                        {formatCount(device.rawSentenceCount)}
                      </td>
                      <td className="px-4 py-3 text-right text-white font-mono font-bold">
                        {formatCount(total)}
                      </td>
                    </tr>
                  );
                })}
                {data.devices.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                      <CircleStackIcon className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      No devices with data found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-slate-400">
            <div className="flex items-center gap-4">
              <PageSizeSelect value={pageSize} onChange={setPageSize} />
              <span>Page {page} of {totalPages || 1}</span>
            </div>
            {(data.total ?? 0) > pageSize && (
              <div className="flex gap-2">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                  className="rounded bg-slate-700 px-3 py-1 disabled:opacity-30 hover:bg-slate-600"
                >
                  Previous
                </button>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="rounded bg-slate-700 px-3 py-1 disabled:opacity-30 hover:bg-slate-600"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
